import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { Volume2, Loader2, RotateCcw, Mic, Send, Upload, FileCheck2, ArrowRight, Keyboard, X } from "lucide-react";
import AvatarCanvas from "../components/AvatarCanvas";
import { speak, cancelSpeech, startRecognition, startRemoteRecording } from "../lib/speech";
import { RETURN_TO_VOICE_KEY } from "../lib/kioskFlow";
import api, { formatApiErrorDetail } from "../lib/api";

// Messaggio di fallback quando l'azione non ha un outcome configurato per instradare
// (vedi onButton sotto): con azioni generiche non c'è più un vocabolario di dominio da
// descrivere — solo l'errore, se presente. Il resto del feedback viene dagli "esiti"
// configurati sull'elemento (ProjectFlowElement.outcomes, vedi action_runner.py).
function describeActionResult(result) {
  if (!result) return null;
  return result.error || null;
}

// Una lista di varianti (step.tts / element.button_tts) -> una scelta a caso; una sola voce
// resta sempre quella. Tollera anche la vecchia forma a stringa singola e liste vuote/assenti.
function pickVariant(list) {
  if (typeof list === "string") return list || null;
  if (!Array.isArray(list) || list.length === 0) return null;
  if (list.length === 1) return list[0];
  return list[Math.floor(Math.random() * list.length)];
}

function hexToRgba(hex, alpha) {
  const h = (hex || "#000000").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const r = parseInt(full.substring(0, 2), 16) || 0;
  const g = parseInt(full.substring(2, 4), 16) || 0;
  const b = parseInt(full.substring(4, 6), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Solo it/en per ora (vedi backend/schemas.py::SUPPORTED_LANGUAGES) — mappa verso i codici
// BCP47 usati da TTS/STT (Web Speech e provider cloud).
const LANG_BCP47 = { it: "it-IT", en: "en-US" };

// Testo del suggerimento nella lingua corrente — ricade sull'italiano, poi sulla prima
// traduzione disponibile, se quella scelta dal visitatore non è stata compilata.
function suggestedPromptText(prompt, uiLang) {
  const t = prompt?.translations || {};
  return t[uiLang] || t.it || Object.values(t)[0] || "";
}

export default function ProjectKiosk() {
  const { projectId } = useParams();
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [currentKey, setCurrentKey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [speaking, setSpeaking] = useState(false);
  const [reply, setReply] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [sending, setSending] = useState(false);
  const [phase, setPhase] = useState("voice");
  const [chatInput, setChatInput] = useState("");
  const [listening, setListening] = useState(false);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [uploading, setUploading] = useState({});
  const [stepText, setStepText] = useState(null);
  const [uiLang, setUiLang] = useState("it");
  const recRef = useRef(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (phase === "voice" && history.length) {
      chatEndRef.current?.scrollIntoView({ block: "end" });
    }
  }, [history, phase]);

  const newSession = () =>
    (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

  const stepsByKey = useMemo(() => {
    const m = {};
    (config?.steps || []).forEach((s) => (m[s.key] = s));
    return m;
  }, [config]);

  const currentStep = currentKey ? stepsByKey[currentKey] : null;
  const lang = LANG_BCP47[uiLang] || config?.avatar?.tts_lang || "it-IT";
  const voice = config?.avatar?.tts_voice;
  const suggestedPrompts = config?.suggested_prompts || [];
  const backgroundUrl = config?.avatar?.background_url;
  const modelScale = config?.avatar?.model_scale;
  const modelOffsetY = config?.avatar?.model_offset_y;
  const formTextColor = config?.avatar?.form_text_color || "#FFFFFF";
  const formInputColor = config?.avatar?.form_input_color || "#1E1E1E";
  const formButtonColor = config?.avatar?.form_button_color || "#0055FF";
  const formOpacity = config?.avatar?.form_opacity ?? 0.55;
  const formPanelBg = hexToRgba("#0A0A0A", formOpacity);
  const ttsCloudEnabled = !!config?.tts_cloud_enabled;
  const sttCloudEnabled = !!config?.stt_cloud_enabled;

  useEffect(() => {
    api
      .get(`/api/projects/public/${projectId}`)
      .then(({ data }) => {
        setConfig(data);
        const start = (data.steps || []).find((s) => s.is_start) || data.steps?.[0];
        setCurrentKey(start?.key || null);
        setSessionId(newSession());
        // Lingua iniziale del selettore dedotta dalla lingua TTS configurata per il progetto —
        // il visitatore può comunque cambiarla dal selettore in schermata iniziale.
        const base = (data.avatar?.tts_lang || "it").slice(0, 2).toLowerCase();
        setUiLang(base === "en" ? "en" : "it");
      })
      .catch((e) => setError(e.response?.data?.detail || "Agente non disponibile"));
  }, [projectId]);

  const say = useCallback(
    (texts, onEnd) => {
      speak(texts, {
        lang, voiceName: voice,
        useRemote: ttsCloudEnabled, projectId,
        onStart: () => setSpeaking(true),
        onEnd: () => { setSpeaking(false); onEnd && onEnd(); },
      });
    },
    [lang, voice, ttsCloudEnabled, projectId]
  );

  const justEnteredFormRef = useRef(false);

  useEffect(() => {
    if (phase === "form" && currentStep) {
      setValidationError(null);
      const text = pickVariant(currentStep.tts);
      setStepText(text);
      const withWelcome = justEnteredFormRef.current;
      justEnteredFormRef.current = false;
      say(withWelcome ? [config?.avatar?.welcome_message, text] : text);
    }
  }, [currentKey, phase]); // eslint-disable-line

  useEffect(() => {
    if (phase !== "form" || !currentStep?.auto_return_seconds) return;
    const timer = setTimeout(() => restart(), currentStep.auto_return_seconds * 1000);
    return () => clearTimeout(timer);
  }, [currentKey, phase]); // eslint-disable-line

  // Rete di sicurezza: si dovrebbe entrare in "form" solo tramite enterForm(), che oggi è
  // nascosto quando il progetto non ha alcuno step (vedi il pulsante "Inizia" sotto) — ma se
  // per qualunque motivo ci si ritrova in questa fase senza uno step valido (flusso svuotato
  // mentre una sessione era già a metà, stato non aggiornato, ecc.), si torna alla voce invece
  // di restare bloccati sullo spinner o rompere il render sugli elementi dello step.
  useEffect(() => {
    if (config && phase === "form" && !currentStep) {
      setPhase("voice");
    }
  }, [config, phase, currentStep]);

  const enterForm = () => {
    cancelSpeech();
    justEnteredFormRef.current = true;
    setPhase("form");
  };

  const setAnswer = (k, v) => {
    setValidationError(null);
    setAnswers((a) => ({ ...a, [k]: v }));
  };

  const validate = (step) => {
    for (const el of step.elements) {
      if (el.kind === "text" && el.required && !(answers[el.field_key] || "").trim()) {
        return `Compila: ${el.label}`;
      }
      if (el.kind === "checkbox" && el.required && !answers[el.field_key]) {
        return `Obbligatorio: ${el.label}`;
      }
      if (el.kind === "file" && el.required && !answers[el.field_key]) {
        return `Carica il documento: ${el.label}`;
      }
    }
    return null;
  };

  const uploadFile = async (el, fileObj) => {
    if (!fileObj) return;
    setValidationError(null);
    const fd = new FormData();
    fd.append("file", fileObj);
    if (sessionId) fd.append("session_id", sessionId);
    if (el.field_key) fd.append("field_key", el.field_key);
    setUploading((u) => ({ ...u, [el.field_key]: true }));
    try {
      const { data } = await api.post(`/api/projects/public/${projectId}/upload`, fd);
      setAnswer(el.field_key, data.id);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail);
      setValidationError(msg);
      say(msg);
    } finally {
      setUploading((u) => ({ ...u, [el.field_key]: false }));
    }
  };

  const goTo = (key) => {
    if (key === RETURN_TO_VOICE_KEY) {
      restart();
      return;
    }
    if (key && stepsByKey[key]) setCurrentKey(key);
  };

  const onButton = async (el) => {
    const err = validate(currentStep);
    if (err) {
      setValidationError(err);
      say(err);
      return;
    }
    setValidationError(null);
    setReply(null);
    const utterances = [];
    const buttonTts = pickVariant(el.button_tts);
    if (buttonTts) utterances.push(buttonTts);

    let actionFailed = false;
    let outcomeConf = null;
    let nextStepKey = el.next_step_key;
    if (el.action) {
      setSending(true);
      try {
        const { data: result } = await api.post(`/api/projects/public/${projectId}/actions/${el.action}`, {
          fields: answers,
        });
        if (result?.outcome && el.outcomes && el.outcomes[result.outcome]) {
          outcomeConf = el.outcomes[result.outcome];
          const msg = pickVariant(outcomeConf.tts);
          if (msg) {
            setReply(msg);
            utterances.push(msg);
          }
          nextStepKey = outcomeConf.next_step_key || null;
        } else {
          const desc = describeActionResult(result);
          if (result?.error) actionFailed = true;
          if (desc) {
            setReply(desc);
            utterances.push(desc);
          }
        }
      } catch (e) {
        actionFailed = true;
        const msg = formatApiErrorDetail(e.response?.data?.detail) || "Azione non riuscita, riprova.";
        setValidationError(msg);
        utterances.push(msg);
      } finally {
        setSending(false);
      }
    }

    if (!actionFailed && el.button_message) {
      setSending(true);
      try {
        const { data } = await api.post("/api/projects/public/message", {
          project_id: projectId, message: el.button_message, session_id: sessionId, lang: uiLang,
        });
        if (data?.reply) {
          setReply(data.reply);
          utterances.push(data.reply);
        }
      } catch {
        /* ignore */
      } finally {
        setSending(false);
      }
    }

    if (actionFailed) {
      if (utterances.length) say(utterances);
      return;
    }
    if (utterances.length) say(utterances, () => goTo(nextStepKey));
    else goTo(nextStepKey);
  };

  const restart = () => {
    cancelSpeech();
    setAnswers({});
    setReply(null);
    setValidationError(null);
    setHistory([]);
    setSessionId(newSession());
    setPhase("voice");
    const start = (config?.steps || []).find((s) => s.is_start) || config?.steps?.[0];
    setCurrentKey(start?.key || null);
  };

  const sendChat = async (text) => {
    const msg = (text || "").trim();
    if (!msg || sending) return;
    setChatInput("");
    const h = [...history, { role: "user", content: msg }];
    setHistory(h);
    setSending(true);
    try {
      const { data } = await api.post("/api/projects/public/message", {
        project_id: projectId, message: msg, history: h.slice(-10), session_id: sessionId, lang: uiLang,
      });
      if (data?.reply) {
        setReply(data.reply);
        setHistory([...h, { role: "assistant", content: data.reply }]);
        say(data.reply);
      }
    } catch {
      /* ignore */
    } finally {
      setSending(false);
    }
  };

  const toggleMic = () => {
    if (listening) {
      recRef.current?.stop?.();
      setListening(false);
      return;
    }
    cancelSpeech();
    const onResult = (t) => { setListening(false); sendChat(t); };
    const onEnd = () => setListening(false);
    // Provider cloud configurato per il progetto: push-to-talk (registra finché non si
    // riclicca). Browser: riconoscimento continuo, si ferma da solo al silenzio.
    const rec = sttCloudEnabled
      ? startRemoteRecording(projectId, lang, { onResult, onEnd })
      : startRecognition(lang, { onResult, onEnd });
    recRef.current = rec;
    if (rec) setListening(true);
  };

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-kiosk-bg text-white">
        <p data-testid="kiosk-error">{error}</p>
      </div>
    );
  }
  if (!config || (phase === "form" && !currentStep)) {
    // Il layer 1 "voce" (sotto) non dipende da currentStep — funziona anche a progetto senza
    // alcun flusso configurato. Solo il layer 2 "form" ne ha bisogno per forza: se si arriva qui
    // in phase="form" senza step (caso limite, impedito nel resto normale nascondendo "Inizia"
    // quando non c'è flusso — vedi sotto), l'effect qui sopra riporta alla voce; nel frattempo
    // resta lo spinner invece di rompere il render su currentStep.elements.
    return (
      <div className="flex h-screen items-center justify-center bg-kiosk-bg text-white">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // ---------- Layer 1: avatar + microfono ----------
  if (phase === "voice") {
    return (
      <div className="relative flex h-screen flex-col items-center bg-kiosk-bg text-white" data-testid="kiosk-root" data-kiosk-phase="voice">
        <AvatarCanvas
          provider={config.avatar.provider} modelUrl={config.avatar.model_url} backgroundUrl={backgroundUrl}
          speaking={speaking} modelScale={modelScale} modelOffsetY={modelOffsetY} still={!speaking}
          className="absolute inset-0 h-full w-full"
        />

        <div className="absolute left-6 top-6 z-10 flex gap-1 sm:left-10 sm:top-10" data-testid="kiosk-lang-selector">
          {Object.keys(LANG_BCP47).map((code) => (
            <button
              key={code}
              onClick={() => setUiLang(code)}
              data-testid={`kiosk-lang-${code}`}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${uiLang === code ? "text-white" : "text-white/60 hover:text-white/90"}`}
              style={{ backgroundColor: uiLang === code ? formButtonColor : "rgba(255,255,255,0.1)" }}
            >
              {code}
            </button>
          ))}
        </div>

        <div className="relative z-10 mt-10 flex flex-col items-center gap-2 px-6 text-center">
          <span className="font-display text-sm uppercase tracking-[0.3em] text-white/60">{config.project_name}</span>
          {!config.avatar.hide_welcome_text && (
            <h1 className="mt-4 max-w-lg font-display text-3xl font-black leading-tight" data-testid="kiosk-welcome-title">
              {config.avatar.welcome_message}
            </h1>
          )}
          {speaking && (
            <span className="mt-2 flex items-center gap-2" style={{ color: formButtonColor }} data-testid="kiosk-speaking">
              <Volume2 className="h-4 w-4 animate-pulse" /> Sto parlando…
            </span>
          )}
        </div>

        {history.length === 0 && suggestedPrompts.length > 0 && (
          <div className="relative z-10 mt-6 flex max-w-xl flex-wrap items-center justify-center gap-2 px-6" data-testid="kiosk-suggested-prompts">
            {suggestedPrompts.map((p) => {
              const text = suggestedPromptText(p, uiLang);
              if (!text) return null;
              return (
                <button
                  key={p.id} onClick={() => sendChat(text)} disabled={sending}
                  data-testid={`kiosk-suggestion-${p.id}`}
                  className="border px-4 py-2 text-sm font-medium text-white/90 transition-colors hover:bg-white/10 disabled:opacity-50"
                  style={{ borderColor: hexToRgba(formButtonColor, 0.5) }}
                >
                  {text}
                </button>
              );
            })}
          </div>
        )}

        {history.length > 0 && (
          <div
            className="absolute inset-x-0 bottom-0 z-10 flex h-[50vh] flex-col overflow-y-auto rounded-t-3xl px-6 pb-6 pt-8 backdrop-blur-xl sm:px-10"
            style={{ backgroundColor: formPanelBg, borderTop: `1px solid ${hexToRgba(formTextColor, 0.14)}` }}
            data-testid="kiosk-chat-transcript"
          >
            <div className="mx-auto mt-auto flex w-full max-w-lg flex-col gap-2.5">
              {history.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className="max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-snug"
                    style={m.role === "user" ? { backgroundColor: formButtonColor, color: "#fff" } : { backgroundColor: hexToRgba(formTextColor, 0.12), color: formTextColor }}
                    data-testid={`kiosk-chat-bubble-${m.role}`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
          </div>
        )}

        <div className="absolute right-6 top-6 z-10 flex flex-col items-end gap-3 sm:right-10 sm:top-10">
          <button
            onClick={toggleMic} data-testid="kiosk-mic-btn"
            className={`flex h-16 w-16 items-center justify-center rounded-full bg-white text-black shadow-lg transition-transform ${listening ? "scale-110" : "hover:scale-105"}`}
            style={listening ? { boxShadow: `0 0 0 4px ${formButtonColor}` } : undefined}
          >
            <Mic className={`h-7 w-7 ${listening ? "animate-pulse" : ""}`} />
          </button>
          {listening && <p className="text-xs" style={{ color: formButtonColor }} data-testid="kiosk-listening">In ascolto…</p>}

          {config.steps?.length > 0 && (
            <button
              onClick={enterForm} data-testid="kiosk-trigger-btn"
              className="flex items-center gap-2 px-6 py-3 font-display text-sm font-bold text-white transition-transform hover:scale-[1.03] active:scale-95"
              style={{ backgroundColor: formButtonColor }}
            >
              Inizia <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // ---------- Layer 2: form logic (azioni deterministiche) ----------
  const inputBorder = hexToRgba(formTextColor, 0.15);
  const panelBorder = hexToRgba(formTextColor, 0.14);
  return (
    <div className="relative flex h-screen flex-col bg-kiosk-bg text-white" data-testid="kiosk-root" data-kiosk-phase="form">
      <AvatarCanvas
        provider={config.avatar.provider} modelUrl={config.avatar.model_url} backgroundUrl={backgroundUrl}
        speaking={speaking} modelScale={modelScale} modelOffsetY={modelOffsetY} still={!speaking}
        className="absolute inset-0 h-full w-full"
      />

      <div className="absolute right-4 top-6 z-20 flex flex-col items-center gap-3 sm:right-8 sm:top-10">
        <button
          onClick={toggleMic} data-testid="kiosk-mic-btn"
          className={`flex h-14 w-14 items-center justify-center rounded-full bg-white text-black shadow-lg transition-transform ${listening ? "scale-110" : "hover:scale-105"}`}
          style={listening ? { boxShadow: `0 0 0 4px ${formButtonColor}` } : undefined}
        >
          <Mic className={`h-6 w-6 ${listening ? "animate-pulse" : ""}`} />
        </button>
        <button onClick={() => setKeyboardOpen(true)} data-testid="kiosk-keyboard-btn" className="flex h-14 w-14 items-center justify-center rounded-full bg-white text-black shadow-lg transition-transform hover:scale-105">
          <Keyboard className="h-6 w-6" />
        </button>
        <button onClick={restart} data-testid="kiosk-restart-icon" className="flex h-14 w-14 items-center justify-center rounded-full bg-white text-black shadow-lg transition-transform hover:scale-105">
          <RotateCcw className="h-6 w-6" />
        </button>
        {listening && <p className="text-center text-xs" style={{ color: formButtonColor }} data-testid="kiosk-listening">In ascolto…</p>}
      </div>

      {keyboardOpen && (
        <div className="absolute inset-0 z-30 flex flex-col bg-black/60 backdrop-blur-sm" data-testid="kiosk-keyboard-overlay">
          <div className="flex h-1/2 flex-col">
            <button onClick={() => setKeyboardOpen(false)} data-testid="kiosk-keyboard-close" className="ml-auto mr-4 mt-4 flex items-center gap-1 text-xs uppercase tracking-widest text-white/70 hover:text-white">
              <X className="h-4 w-4" /> Chiudi
            </button>
            <div className="mt-auto w-full px-6 pb-6">
              <div className="mx-auto w-full max-w-lg">
                <p className="mb-2 text-xs uppercase tracking-widest text-white/60">Scrivi il tuo messaggio</p>
                <div className="flex gap-2">
                  <input
                    autoFocus value={chatInput} onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && chatInput.trim()) { sendChat(chatInput); setKeyboardOpen(false); } }}
                    placeholder="Scrivi qui…" data-testid="kiosk-chat-input"
                    className="flex-1 border px-4 py-3 text-base placeholder:opacity-40 focus:outline-none"
                    style={{ backgroundColor: formInputColor, color: formTextColor, borderColor: inputBorder }}
                  />
                  <button
                    onClick={() => { sendChat(chatInput); setKeyboardOpen(false); }}
                    disabled={sending || !chatInput.trim()} data-testid="kiosk-chat-send"
                    className="flex w-14 items-center justify-center text-white disabled:opacity-40" style={{ backgroundColor: formButtonColor }}
                  >
                    {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="h-1/2" />
        </div>
      )}

      <div
        className="absolute inset-x-0 bottom-0 z-10 h-[50vh] overflow-y-auto rounded-t-3xl px-6 py-6 backdrop-blur-xl sm:px-10 sm:py-7"
        style={{ backgroundColor: formPanelBg, borderTop: `1px solid ${panelBorder}`, color: formTextColor }}
        data-testid="kiosk-form-panel"
      >
        <div className="mx-auto w-full max-w-lg" data-testid={`kiosk-step-${currentStep.key}`}>
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs uppercase tracking-[0.3em] opacity-60">{config.project_name}</span>
            <p className="text-xs font-semibold uppercase tracking-[0.3em]" style={{ color: formButtonColor }}>{currentStep.title}</p>
          </div>
          {stepText && <h2 className="mt-2 font-display text-xl font-bold leading-snug opacity-90">{stepText}</h2>}
          {speaking && (
            <span className="mt-2 flex items-center gap-2 text-xs" style={{ color: formButtonColor }} data-testid="kiosk-speaking">
              <Volume2 className="h-3.5 w-3.5 animate-pulse" /> Sto parlando…
            </span>
          )}

          {reply && (
            <div className="mt-3 border-l-2 p-3 text-sm opacity-90" style={{ borderColor: formButtonColor, backgroundColor: hexToRgba(formTextColor, 0.06) }} data-testid="kiosk-reply">
              {reply}
            </div>
          )}

          {validationError && (
            <div className="mt-3 border-l-2 border-red-500 bg-red-500/10 p-3 text-sm text-red-300" data-testid="kiosk-validation-error">
              {validationError}
            </div>
          )}

          <div className="mt-4 space-y-2.5">
            {currentStep.elements.map((el) => {
              if (el.kind === "text") {
                return (
                  <div key={el.id} className="space-y-1">
                    <label className="text-xs opacity-60">{el.label}</label>
                    <input
                      value={answers[el.field_key] || ""} onChange={(e) => setAnswer(el.field_key, e.target.value)}
                      placeholder={el.placeholder || ""} data-testid={`kiosk-field-${el.field_key}`}
                      className="w-full border px-4 py-2.5 text-base placeholder:opacity-40 focus:outline-none"
                      style={{ backgroundColor: formInputColor, color: formTextColor, borderColor: inputBorder }}
                    />
                  </div>
                );
              }
              if (el.kind === "checkbox") {
                return (
                  <label key={el.id} className="flex cursor-pointer items-start gap-3 text-sm opacity-80">
                    <input
                      type="checkbox" checked={!!answers[el.field_key]} onChange={(e) => setAnswer(el.field_key, e.target.checked)}
                      data-testid={`kiosk-checkbox-${el.field_key}`} className="mt-1 h-5 w-5" style={{ accentColor: formButtonColor }}
                    />
                    <span>{el.label}</span>
                  </label>
                );
              }
              if (el.kind === "file") {
                const done = !!answers[el.field_key];
                const busy = !!uploading[el.field_key];
                return (
                  <div key={el.id} className="space-y-1">
                    <label className="text-xs opacity-60">{el.label}</label>
                    <label
                      data-testid={`kiosk-file-${el.field_key}`}
                      className="flex cursor-pointer items-center gap-3 border px-4 py-3 text-base transition-colors"
                      style={{ backgroundColor: done ? hexToRgba(formButtonColor, 0.15) : formInputColor, borderColor: done ? formButtonColor : inputBorder }}
                    >
                      {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : done ? <FileCheck2 className="h-5 w-5" style={{ color: formButtonColor }} /> : <Upload className="h-5 w-5 opacity-70" />}
                      <span className="opacity-90">
                        {busy ? "Caricamento…" : done ? "Documento caricato ✓ (tocca per sostituire)" : el.placeholder || "Tocca per caricare o scattare una foto"}
                      </span>
                      <input type="file" accept="image/*,application/pdf" capture="environment" className="hidden" disabled={busy} onChange={(e) => uploadFile(el, e.target.files?.[0])} />
                    </label>
                  </div>
                );
              }
              return (
                <button
                  key={el.id} onClick={() => onButton(el)} disabled={sending} data-testid={`kiosk-button-${el.id}`}
                  className="flex w-full items-center justify-between border px-5 py-3.5 text-left font-display text-base font-semibold transition-opacity hover:opacity-80 disabled:opacity-50"
                  style={{ backgroundColor: hexToRgba(formButtonColor, 0.18), borderColor: hexToRgba(formButtonColor, 0.4) }}
                >
                  {el.label}
                  {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
